import { z } from 'zod';

export const PlatformType = z.enum(['WEB', 'EXT', 'APP', 'UNKNOWN']);
export type PlatformType = z.infer<typeof PlatformType>;

export const PlatformAlias = z.enum([
	'WEB',
	'WEB_ADMIN',
	'EXT_CHROME',
	'EXT_FIREFOX',
	'EXT_OPERA',
	'EXT_SAFARI',
	'EXT_EDGE',
	'APP_IOS',
	'APP_ANDROID',
	'UNKNOWN',
]);

export type PlatformAlias = z.infer<typeof PlatformAlias>;

export const PlatformInfoFromAlias = PlatformAlias.transform(
	(alias): { platform_type: PlatformType; platform_alias: PlatformAlias } => {
		const [type] = alias.split('_');

		return {
			platform_type: PlatformType.parse(type),
			platform_alias: alias,
		};
	},
);
export type PlatformInfoFromAlias = z.infer<typeof PlatformInfoFromAlias>;

export type PlatformDisplayInfo = {
	shortLabel: string;
	longLabel: string;
};

const PLATFORM_DISPLAY_OVERRIDES: Partial<Record<PlatformAlias, PlatformDisplayInfo>> = {
	WEB: {
		shortLabel: 'Web',
		longLabel: 'Web',
	},
	WEB_ADMIN: {
		shortLabel: 'Admin',
		longLabel: 'Admin Web',
	},
	APP_IOS: {
		shortLabel: 'iOS',
		longLabel: 'iOS App',
	},
	APP_ANDROID: {
		shortLabel: 'Android',
		longLabel: 'Android App',
	},
};

const PLATFORM_TYPE_LABELS: Record<PlatformType, string> = {
	APP: 'App',
	WEB: 'Web',
	EXT: 'Extension',
	UNKNOWN: 'Unknown',
};

const PLATFORM_SUFFIX_LABEL_OVERRIDES = {
	IOS: 'iOS',
} as const;

function isPlatformType(token: string): token is PlatformType {
	return PlatformType.options.includes(token as PlatformType);
}

function formatPlatformToken(token: string): string {
	if (isPlatformType(token)) {
		return PLATFORM_TYPE_LABELS[token];
	}

	if (token in PLATFORM_SUFFIX_LABEL_OVERRIDES) {
		return PLATFORM_SUFFIX_LABEL_OVERRIDES[token as keyof typeof PLATFORM_SUFFIX_LABEL_OVERRIDES];
	}

	return `${token.slice(0, 1)}${token.slice(1).toLowerCase()}`;
}

export function getPlatformDisplayInfo(alias: PlatformAlias | string): PlatformDisplayInfo {
	const override = PLATFORM_DISPLAY_OVERRIDES[alias as PlatformAlias];

	if (override) {
		return override;
	}

	const rawTokens = alias.split('_').filter(Boolean);

	if (rawTokens.length === 0) {
		return {
			shortLabel: alias,
			longLabel: alias,
		};
	}

	const [prefixToken = alias, ...restTokens] = rawTokens;
	const prefixLabel = formatPlatformToken(prefixToken);
	const restLabel = restTokens.map(formatPlatformToken).join(' ');

	if (!restLabel) {
		return {
			shortLabel: prefixLabel,
			longLabel: prefixLabel,
		};
	}

	if (prefixToken === 'APP') {
		return {
			shortLabel: restLabel,
			longLabel: `${restLabel} App`,
		};
	}

	if (prefixToken === 'EXT') {
		return {
			shortLabel: restLabel,
			longLabel: `${restLabel} Extension`,
		};
	}

	if (prefixToken === 'WEB') {
		return {
			shortLabel: restLabel,
			longLabel: `${restLabel} Web`,
		};
	}

	return {
		shortLabel: restLabel,
		longLabel: `${prefixLabel} ${restLabel}`,
	};
}

export function getPlatformDisplayLabel(alias: PlatformAlias | string): string {
	return getPlatformDisplayInfo(alias).longLabel;
}
